import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  CircularProgress,
  Box,
  Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { resolvePygSerieDetalle } from "./pygSerieDetalleConfig";
import { MESES } from "./pygAnaliticoBuild";
import { useTranslation } from "react-i18next";
import { translatePygLine } from "../../constants/pygLineI18n";

const API_URL = process.env.REACT_APP_API_URL;

const formatearImporte = (valor) =>
  (valor ?? 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

const renderMes = (mes) =>
  mes >= 1 && mes <= 12 ? MESES[mes - 1] : mes || "—";

/** Fila USD cuyo importe en vista venía en moneda documento (BASE = BASEMONEDA). */
const filaUsdDocumento = (row) => {
  const mon = String(row.codmon || "")
    .trim()
    .toUpperCase();
  if (!mon || ["EURO", "EUR"].includes(mon)) return false;
  const base = Number(row.base) || 0;
  const doc = Number(row.baseDocumento ?? row.base) || 0;
  return Math.abs(doc - base) > 0.02;
};

const PygSerieDetalleDialog = ({
  open,
  onClose,
  titulo,
  year,
  empresa,
  nivel1,
  omitirGeneralStage,
  nivel2,
  mes,
  vistaLabel,
  totalEsperado,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const resolved = resolvePygSerieDetalle(titulo);
  const config = resolved?.config || {};
  const isProyeccion = config.variant === "proyeccion";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mesLabel =
    mes != null && mes >= 1 && mes <= 12 ? MESES[mes - 1] : null;
  const showOrigen = empresa === "fusion";

  useEffect(() => {
    if (!open || !config.endpoint || !year) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);

    let url = `${API_URL}/api/pyg/${config.endpoint}?year=${year}&empresa=${encodeURIComponent(empresa)}`;
    if (nivel1) url += `&nivel1=${encodeURIComponent(nivel1)}`;
    if (omitirGeneralStage) url += "&omitirGeneralStage=1";
    if (nivel2) url += `&nivel2=${encodeURIComponent(nivel2)}`;
    if (mes != null && mes >= 1 && mes <= 12) url += `&mes=${mes}`;

    fetch(url, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setRows(Array.isArray(data) ? data : []);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || t("pygAnalitico.detail.error"));
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    config.endpoint,
    year,
    empresa,
    nivel1,
    omitirGeneralStage,
    nivel2,
    mes,
  ]);

  const totalBase = useMemo(
    () => rows.reduce((acc, row) => acc + (Number(row.base) || 0), 0),
    [rows],
  );

  const rowKey = (row, idx) => {
    if (isProyeccion) {
      return `${row.origen}-${row.mes}-${row.codart}-${row.numdoc}-${idx}`;
    }
    return `${row.origen}-${row.serie}-${row.numdoc}-${row.referencia}-${idx}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={800}>
          {translatePygLine(t, titulo)}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
          <Chip size="small" label={t("pygAnalitico.detail.year", { year })} />
          {vistaLabel ? <Chip size="small" label={vistaLabel} /> : null}
          {mesLabel ? (
            <Chip size="small" color="primary" label={mesLabel} />
          ) : (
            <Chip size="small" variant="outlined" label={t("pygAnalitico.detail.fullYear")} />
          )}
          {nivel2 ? (
            <Chip size="small" label={t("pygAnalitico.detail.brand", { brand: nivel2 })} />
          ) : null}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {t("pygAnalitico.detail.empty")}
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: "65vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {showOrigen ? (
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t("pygAnalitico.detail.origin")}
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pygAnalitico.detail.month")}
                  </TableCell>
                  {isProyeccion ? (
                    <>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("pygAnalitico.detail.itemCode")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {config.terceroLabel === "Proveedor"
                          ? t("pygAnalitico.detail.offerIdPurchases")
                          : t("pygAnalitico.detail.offerIdSales")}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("pygAnalitico.detail.series")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("pygAnalitico.detail.docNo")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("pygAnalitico.detail.reference")}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontWeight: 700 }}>
                    {config.terceroLabel === "Proveedor"
                      ? t("pygAnalitico.detail.supplier")
                      : t("pygAnalitico.detail.customer")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pygAnalitico.detail.sds")}
                  </TableCell>
                  {isProyeccion ? (
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t("pygAnalitico.detail.status")}
                    </TableCell>
                  ) : null}
                  {!isProyeccion && config.showRepresentante ? (
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t("pygAnalitico.detail.rep")}
                    </TableCell>
                  ) : null}
                  {config.showDivisa ? (
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t("pygAnalitico.detail.currency")}
                    </TableCell>
                  ) : null}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {t("pygAnalitico.detail.baseEur")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow
                    key={rowKey(row, idx)}
                    hover
                    sx={
                      filaUsdDocumento(row)
                        ? {
                            backgroundColor: alpha(
                              theme.palette.warning.main,
                              0.1,
                            ),
                          }
                        : undefined
                    }
                  >
                    {showOrigen ? (
                      <TableCell>{row.origen || "—"}</TableCell>
                    ) : null}
                    <TableCell>{renderMes(row.mes)}</TableCell>
                    {isProyeccion ? (
                      <>
                        <TableCell>{row.codart || "—"}</TableCell>
                        <TableCell>{row.numdoc || "—"}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{row.serie || "—"}</TableCell>
                        <TableCell>{row.numdoc || "—"}</TableCell>
                        <TableCell>{row.referencia || "—"}</TableCell>
                      </>
                    )}
                    <TableCell>{row.tercero || "—"}</TableCell>
                    <TableCell>{row.sds || "—"}</TableCell>
                    {isProyeccion ? (
                      <TableCell>{row.situacion || "—"}</TableCell>
                    ) : null}
                    {!isProyeccion && config.showRepresentante ? (
                      <TableCell>{row.representante || "—"}</TableCell>
                    ) : null}
                    {config.showDivisa ? (
                      <TableCell>{row.codmon || "—"}</TableCell>
                    ) : null}
                    <TableCell align="right">
                      {formatearImporte(row.base)}
                      {config.showDivisa &&
                      row.codmon &&
                      !["EURO", "EUR"].includes(
                        String(row.codmon).trim().toUpperCase(),
                      ) &&
                      Math.abs(
                        Number(row.baseDocumento || 0) - Number(row.base || 0),
                      ) > 0.02 ? (
                        <Typography
                          component="span"
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ fontSize: "10px", lineHeight: 1.1 }}
                        >
                          {t("pygAnalitico.detail.docAmount", {
                            amount: formatearImporte(row.baseDocumento),
                          })}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && rows.length > 0 ? (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {t("pygAnalitico.detail.linesTotal", {
                count: rows.length,
                total: formatearImporte(totalBase),
              })}
            </Typography>
            {totalEsperado != null &&
            Math.abs(totalBase - Number(totalEsperado)) > 0.02 ? (
              <Typography variant="caption" color="warning.main">
                {t("pygAnalitico.detail.cellTable", {
                  amount: formatearImporte(totalEsperado),
                })}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose}>{t("pygAnalitico.detail.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PygSerieDetalleDialog;
